import React, { useState, useEffect } from "react";
import PlayerNameForm from "./components/PlayerNameForm";
import RobotJumpGame from "./components/RobotJumpGame";
import PrizeWheel from "./components/PrizeWheel";
import Leaderboard from "./components/Leaderboard";
import { gameAPI } from "./services/api";
import "./App.css";

function App() {
  const [gameState, setGameState] = useState("name"); // name, playing, gameOver, spinning, complete, salesMessage
  const [sessionToken, setSessionToken] = useState(null);
  const [playerData, setPlayerData] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [prizes, setPrizes] = useState([]);
  const [wonPrize, setWonPrize] = useState(null);
  const [allSessions, setAllSessions] = useState([]);
  const [recentWinner, setRecentWinner] = useState(null);

  const GAME_SLUG = "robot-jump";
  const MIN_SCORE_FOR_PRIZE = 250;

  useEffect(() => {
    loadGameData();
    loadRecentWinner();
  }, []);

  const loadGameData = async () => {
    try {
      const response = await gameAPI.getGame(GAME_SLUG);
      setGameData(response.data);
      setPrizes(response.data.prizes || []);
    } catch (error) {
      console.error("Error loading game:", error);
    }
  };

  const loadRecentWinner = async () => {
    try {
      const response = await gameAPI.getRecentWinner(GAME_SLUG);
      if (response.data) {
        setRecentWinner(response.data);
      }
    } catch (error) {
      console.error("Error loading recent winner:", error);
    }
  };

  const handlePlayerSubmit = async (formData) => {
    try {
      setPlayerData(formData);
      await startNewGame(formData);
    } catch (error) {
      console.error("Error starting session:", error);
      alert("Error starting game. Please try again.");
    }
  };

  const startNewGame = async (playerInfo = playerData) => {
    try {
      const response = await gameAPI.startSession(GAME_SLUG, playerInfo);
      setSessionToken(response.data.session_token);
      setGameState("playing");
    } catch (error) {
      console.error("Error starting session:", error);
      alert("Error starting game. Please try again.");
    }
  };

  const handleGameOver = async (scoreData) => {
    try {
      await gameAPI.completeSession(sessionToken, scoreData);
      setAllSessions((prev) => [
        ...prev,
        { sessionToken, score: scoreData.score, ...scoreData },
      ]);
      setGameState("gameOver");
      loadRecentWinner(); // Refresh recent winner
    } catch (error) {
      console.error("Error completing session:", error);
      alert("Error saving score. Please try again.");
    }
  };

  const handlePlayAgain = () => {
    startNewGame();
  };

  const handleFinishPlaying = () => {
    setGameState("spinning");
  };

  const handlePrizeWon = (prize) => {
    setWonPrize(prize);
    setGameState("complete");
  };

  const handleClose = () => {
    setGameState("salesMessage");
  };

  const handleReplay = () => {
    handleResetPlayer();
  };

  const handleContactMe = async () => {
    try {
      await gameAPI.requestContact({
        player_name: playerData.player_name,
        company_name: playerData.company_name,
        player_email: playerData.player_email,
        player_phone: playerData.player_phone,
      });
      alert("Thank you! We'll be in touch soon.");
      handleResetPlayer();
    } catch (error) {
      console.error("Error submitting contact request:", error);
      alert("Thank you for your interest! We'll be in touch soon.");
      handleResetPlayer();
    }
  };

  const handleResetPlayer = () => {
    setGameState("name");
    setSessionToken(null);
    setPlayerData(null);
    setWonPrize(null);
    setAllSessions([]);
  };

  const bestScore =
    allSessions.length > 0
      ? Math.max(...allSessions.map((s) => s.score || 0))
      : 0;

  const isEligibleForPrize = bestScore >= MIN_SCORE_FOR_PRIZE;

  return (
    <div className="App">
      {gameState === "name" && <PlayerNameForm onSubmit={handlePlayerSubmit} />}

      {gameState === "playing" && (
        <RobotJumpGame
          sessionToken={sessionToken}
          playerName={playerData.player_name}
          onGameOver={handleGameOver}
        />
      )}

      {gameState === "gameOver" && (
        <div className="game-over-screen">
          <div className="game-over-card">
            <h1>🎮 Round Complete!</h1>
            <p className="welcome-back">
              Great job, {playerData.player_name}! 👏
            </p>

            {allSessions.length > 0 && (
              <div className="session-stats">
                <h3>📊 Your Stats Today</h3>
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-value">{allSessions.length}</div>
                    <div className="stat-label">Games Played</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{bestScore}</div>
                    <div className="stat-label">Best Score</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">
                      {Math.round(
                        allSessions.reduce(
                          (sum, s) => sum + (s.score || 0),
                          0
                        ) / allSessions.length
                      )}
                    </div>
                    <div className="stat-label">Avg Score</div>
                  </div>
                </div>
              </div>
            )}

            {!isEligibleForPrize && (
              <div className="prize-requirement-notice">
                <p>
                  🎯 Score {MIN_SCORE_FOR_PRIZE}+ to unlock the Prize Wheel!
                </p>
                <p className="score-progress">
                  Current best: {bestScore} / {MIN_SCORE_FOR_PRIZE}
                </p>
              </div>
            )}

            {recentWinner && recentWinner.prize_type !== "no_prize" && (
              <div className="recent-winner">
                <h4>🎉 Recent Winner</h4>
                <p>
                  <strong>{recentWinner.player_name}</strong> from{" "}
                  <strong>{recentWinner.company_name}</strong> just won:{" "}
                  <strong>{recentWinner.prize_name}</strong>
                </p>
              </div>
            )}

            <div className="action-buttons">
              <button onClick={handlePlayAgain} className="play-again-btn">
                🎮 Play Again
              </button>
              {isEligibleForPrize && (
                <button onClick={handleFinishPlaying} className="finish-btn">
                  🎁 Spin Prize Wheel
                </button>
              )}
              <button onClick={handleClose} className="close-btn">
                ✖️ Close
              </button>
            </div>

            <div className="logo-watermark">
              <img
                src="/peach-logo.png"
                alt="Peach"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>

            <div className="leaderboard-section">
              <Leaderboard gameSlug={GAME_SLUG} />
            </div>
          </div>
        </div>
      )}

      {gameState === "spinning" && (
        <div className="spinning-container">
          <PrizeWheel
            sessionToken={allSessions[allSessions.length - 1]?.sessionToken}
            prizes={prizes}
            onPrizeWon={handlePrizeWon}
          />
        </div>
      )}

      {gameState === "complete" && (
        <div className="complete-container">
          <div className="complete-card">
            <h1>🎉 All Done!</h1>
            <p className="thank-you">
              Thank you for playing, {playerData.player_name}!
            </p>

            {wonPrize && wonPrize.prize_type !== "no_prize" && (
              <div className="final-prize">
                <h3>🏆 Your Prize:</h3>
                <h2>{wonPrize.name}</h2>
                <p>{wonPrize.description}</p>
                <div className="final-claim-code">
                  <strong>Claim Code:</strong>
                  <code>{wonPrize.claim_code}</code>
                  <p className="claim-instructions">
                    Save this code to claim your prize!
                  </p>
                </div>
              </div>
            )}

            {allSessions.length > 0 && (
              <div className="final-stats">
                <h3>🎮 Session Summary</h3>
                <div className="stats-summary">
                  <p>
                    <strong>Games Played:</strong> {allSessions.length}
                  </p>
                  <p>
                    <strong>Best Score:</strong>{" "}
                    {Math.max(...allSessions.map((s) => s.score || 0))}
                  </p>
                  <p>
                    <strong>Total Points:</strong>{" "}
                    {allSessions.reduce((sum, s) => sum + (s.score || 0), 0)}
                  </p>
                </div>
              </div>
            )}

            <Leaderboard gameSlug={GAME_SLUG} />

            <div className="final-action-buttons">
              <button className="replay-button" onClick={handleReplay}>
                🔄 Play Again
              </button>
              <button className="close-button" onClick={handleClose}>
                ✖️ Close
              </button>
            </div>
          </div>
        </div>
      )}

      {gameState === "salesMessage" && (
        <div className="sales-message-container">
          <div className="sales-card">
            <div className="logo-container">
              <img
                src="/peach-logo.png"
                alt="Peach"
                className="company-logo-large"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>

            <h1>💡 Liked the experience?</h1>
            <p className="sales-pitch">
              We create innovative, engaging experiences that help businesses
              connect with their target audience in memorable ways.
            </p>
            <p className="sales-pitch">
              From gamification to interactive campaigns, we can help you stand
              out and make lasting impressions with your customers.
            </p>

            <div className="contact-prompt">
              <h3>Interested in working together?</h3>
              <p>Let's discuss how we can create something amazing for you.</p>
            </div>

            <div className="contact-actions">
              <button className="contact-me-btn" onClick={handleContactMe}>
                ✉️ Yes, Contact Me
              </button>
              <button className="no-thanks-btn" onClick={handleResetPlayer}>
                No Thanks
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
